import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreCause, StoreCauseResponse } from './cause.types';

type UpdateStoreCauseAccountCodeRequest = {
  causeId: string;
  accountCode?: string | null;
  storeItemId?: string;
};

type UpdateStoreCauseRequest = {
  causeId: string;
  cause: Partial<StoreCause>;
};

const useUpdateStoreCause = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  const update = async ({ causeId, cause }: UpdateStoreCauseRequest) => {
    const { data } = await StoreApi.put<StoreCauseResponse>(`/cause/${causeId}`, { cause });
    return data.cause;
  };

  return useMutation({
    mutationFn: update,
    onSuccess(_, variables) {
      const { causeId } = variables;
      const key = storeKeys.id(causeId).causes.id(causeId).detail;
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

const useUpdateStoreCausePOSAccountCode = () => {
  const StoreApi = useStoreApi();

  const update = async ({ causeId, accountCode }: UpdateStoreCauseAccountCodeRequest) => {
    const { data } = await StoreApi.put(`/causes/${causeId}/account_code`, {
      cause_id: causeId,
      cause: { pos_account_code: accountCode },
    });
    return data;
  };

  return useMutation({
    mutationFn: update,
  });
};

const useUpdateStoreCausePOSDonationAccountCode = () => {
  const StoreApi = useStoreApi();

  const update = async ({ causeId, accountCode }: UpdateStoreCauseAccountCodeRequest) => {
    const { data } = await StoreApi.put(`/causes/${causeId}/account_code`, {
      cause_id: causeId,
      cause: { pos_donation_account_code: accountCode },
    });
    return data;
  };

  return useMutation({
    mutationFn: update,
  });
};

const useUpdateStoreCauseProductFundraiserAccountCode = () => {
  const StoreApi = useStoreApi();

  const update = async ({ causeId, accountCode }: UpdateStoreCauseAccountCodeRequest) => {
    const { data } = await StoreApi.put(`/causes/${causeId}/account_code`, {
      cause_id: causeId,
      cause: { product_fundraisers_account_code: accountCode },
    });
    return data;
  };

  return useMutation({
    mutationFn: update,
  });
};

type UpdateStoreCauseReceiptMessageRequest = {
  causeId: string;
  customReceiptMessage: string;
  enableCustomReceiptMessage: boolean;
};

const useUpdateStoreCauseReceiptMessage = () => {
  const StoreApi = useStoreApi();

  const update = async ({
    causeId,
    customReceiptMessage,
    enableCustomReceiptMessage,
  }: UpdateStoreCauseReceiptMessageRequest) => {
    const { data } = await StoreApi.put<{
      cause_id: string;
      cause: {
        receipt_message: string;
        add_receipt_message: boolean;
      };
    }>(`/cause/${causeId}`, {
      cause: {
        receipt_message: customReceiptMessage,
        add_receipt_message: enableCustomReceiptMessage,
      },
    });

    return data;
  };

  return useMutation({ mutationFn: update });
};

export {
  useUpdateStoreCause,
  useUpdateStoreCausePOSAccountCode,
  useUpdateStoreCausePOSDonationAccountCode,
  useUpdateStoreCauseProductFundraiserAccountCode,
  useUpdateStoreCauseReceiptMessage,
};
export type { UpdateStoreCauseAccountCodeRequest };
